import React from 'react';
import { Button } from '../ui/button';
import { useNavigate } from 'react-router-dom';
import { Building2, Star, Award, Globe, CheckCircle, ArrowRight, ArrowLeft } from 'lucide-react';
import CorporateHeader from '../CorporateHeader';
import Footer from '../Footer';
import Breadcrumb from '../Breadcrumb';

const BusinessDirectoryPage = () => {
  const navigate = useNavigate();

  const tiers = [
    {
      id: 'basic',
      name: 'Basic Listing',
      price: 'Free',
      features: [
        'Business name & category',
        'Contact information',
        'Basic description',
        'Standard search visibility'
      ],
      cta: 'Get Listed',
      ctaPath: '/start-project',
    },
    {
      id: 'premium',
      name: 'Premium Visibility',
      price: '$49/month',
      popular: true,
      features: [
        'Everything in Basic',
        'Featured placement',
        'Enhanced profile',
        'Social media links',
        'Photo gallery',
        'Priority search ranking',
        'Monthly analytics'
      ],
      cta: 'Upgrade Listing',
      ctaPath: '/start-project',
    },
    {
      id: 'media',
      name: 'Media Exposure',
      price: 'Custom',
      features: [
        'Everything in Premium',
        'Feature article opportunity',
        'Social media spotlight',
        'Newsletter inclusion',
        'Podcast/interview opportunity',
        'Event partnership access'
      ],
      cta: 'Contact Us',
      ctaPath: '/start-project',
    },
  ];

  return (
    <div className="min-h-screen bg-black">
      <CorporateHeader />
      
      {/* Hero */}
      <section className="pt-28 pb-20 bg-gradient-to-b from-red-900/30 via-black to-black relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-red-600/20 via-transparent to-transparent"></div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Breadcrumb Navigation */}
          <Breadcrumb items={[
            { label: 'Divisions', path: null },
            { label: 'Business Directory' }
          ]} />
          
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-red-600 to-rose-600 rounded-xl flex items-center justify-center">
              <Building2 className="w-8 h-8 text-white" />
            </div>
            <div>
              <p className="text-red-400 text-sm font-semibold uppercase tracking-wider">Division</p>
              <h1 className="text-4xl lg:text-5xl font-black text-white">Business Directory</h1>
            </div>
          </div>
          
          <p className="text-xl text-gray-400 max-w-3xl leading-relaxed">
            Visibility and business credibility ecosystem. Get listed in our curated directory of Black-owned businesses and gain access to our media network.
          </p>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-8 text-center">Why Get Listed?</h2>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                { icon: Globe, title: 'Increased Visibility', desc: 'Get discovered by customers actively searching for Black-owned businesses.' },
                { icon: Star, title: 'Credibility Boost', desc: 'Association with our trusted platform enhances your business reputation.' },
                { icon: Award, title: 'Media Opportunities', desc: 'Premium members get access to media features and exposure opportunities.' },
              ].map((item) => {
                const Icon = item.icon;
                return (
                  <div key={item.title} className="text-center p-6 bg-gray-900/50 border border-gray-800 rounded-xl">
                    <div className="w-14 h-14 bg-red-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon className="w-7 h-7 text-red-500" />
                    </div>
                    <h3 className="font-bold text-white mb-2">{item.title}</h3>
                    <p className="text-gray-400 text-sm">{item.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Listing Tiers */}
      <section className="py-16 bg-gradient-to-b from-black to-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">Listing Options</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Choose the visibility level that's right for your business.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {tiers.map((tier) => (
              <div 
                key={tier.id}
                className={`bg-gray-900/50 border rounded-2xl overflow-hidden transition-all duration-300 ${
                  tier.popular 
                    ? 'border-red-600 ring-1 ring-red-600/30 scale-105' 
                    : 'border-gray-800 hover:border-red-600/50'
                }`}
              >
                {tier.popular && (
                  <div className="bg-red-600 text-white text-center py-2 text-sm font-bold">
                    RECOMMENDED
                  </div>
                )}
                
                <div className="p-8">
                  <h3 className="text-xl font-bold text-white mb-2">{tier.name}</h3>
                  <div className="text-3xl font-bold text-red-400 mb-6">{tier.price}</div>
                  
                  <ul className="space-y-3 mb-8">
                    {tier.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start text-gray-300 text-sm">
                        <CheckCircle className="w-5 h-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  
                  <Button 
                    onClick={() => navigate(tier.ctaPath)}
                    className={`w-full font-semibold ${
                      tier.popular 
                        ? 'bg-red-600 hover:bg-red-700 text-white' 
                        : 'bg-gray-800 hover:bg-gray-700 text-white'
                    }`}
                  >
                    {tier.cta}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Coming Soon Features */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <span className="inline-block bg-red-600/20 text-red-500 text-sm font-semibold px-4 py-1 rounded-full mb-4">
              COMING SOON
            </span>
            <h2 className="text-2xl font-bold text-white mb-4">Future Features</h2>
            <p className="text-gray-400 mb-8">
              We're constantly expanding our directory platform with new features to help your business thrive.
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              {['Searchable Directory', 'Customer Reviews', 'Event Listings'].map((feature) => (
                <div key={feature} className="p-4 bg-gray-900/30 border border-gray-800 rounded-lg">
                  <span className="text-gray-500">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-b from-black to-red-900/20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Ready to Get Listed?</h2>
          <p className="text-gray-400 mb-8 max-w-xl mx-auto">
            Join our growing community of Black-owned businesses and increase your visibility today.
          </p>
          <Button 
            onClick={() => navigate('/start-project')}
            className="bg-red-600 hover:bg-red-700 text-white font-bold px-8 py-4 text-lg"
          >
            Apply for Listing
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default BusinessDirectoryPage;
